import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  AccountBalance,
  AccountBalanceWallet,
  TrendingUp,
  TrendingDown,
  Receipt,
  SwapHoriz
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function Tesoreria() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resumen, setResumen] = useState(null);

  useEffect(() => {
    cargarResumen();
  }, []);

  const cargarResumen = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.get(`${API_URL}/api/tesoreria/resumen`, config);
      setResumen(response.data.data);
    } catch (err) {
      console.error('Error al cargar resumen:', err);
      setError('Error al cargar resumen de tesorería');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Tesorería
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<AccountBalanceWallet />}
            onClick={() => navigate('/tesoreria/cajas')}
          >
            Cajas
          </Button>
          <Button
            variant="outlined"
            startIcon={<AccountBalance />}
            onClick={() => navigate('/tesoreria/bancos')}
          >
            Bancos
          </Button>
          <Button
            variant="contained"
            startIcon={<SwapHoriz />}
            onClick={() => navigate('/tesoreria/transacciones')}
          >
            Nueva Transacción
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {resumen && (
        <Grid container spacing={3}>
          {/* Saldo Total */}
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <AccountBalance sx={{ mr: 1 }} />
                  <Typography variant="h6">Saldo Total</Typography>
                </Box>
                <Typography variant="h3">
                  {formatCurrency(resumen.saldo_total)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                  Cajas + Bancos
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Saldo en Cajas */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <AccountBalanceWallet sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6">Cajas</Typography>
                </Box>
                <Typography variant="h4">
                  {formatCurrency(resumen.saldo_cajas)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {resumen.cantidad_cajas} caja(s) activa(s)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Saldo en Bancos */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <AccountBalance sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h6">Bancos</Typography>
                </Box>
                <Typography variant="h4">
                  {formatCurrency(resumen.saldo_bancos)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {resumen.cantidad_bancos} cuenta(s) activa(s)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Flujo del Mes */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Flujo de Caja del Mes
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TrendingUp sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="subtitle1">Ingresos</Typography>
                  </Box>
                  <Typography variant="h5" color="success.main">
                    {formatCurrency(resumen.ingresos_mes)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TrendingDown sx={{ mr: 1, color: 'error.main' }} />
                    <Typography variant="subtitle1">Egresos</Typography>
                  </Box>
                  <Typography variant="h5" color="error.main">
                    {formatCurrency(resumen.egresos_mes)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <SwapHoriz sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1">Flujo Neto</Typography>
                  </Box>
                  <Typography 
                    variant="h5" 
                    color={resumen.flujo_mes >= 0 ? 'success.main' : 'error.main'}
                  >
                    {formatCurrency(resumen.flujo_mes)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Cheques Pendientes */}
          {resumen.cheques_pendientes > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, bgcolor: 'warning.light' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <Receipt sx={{ mr: 2, fontSize: 40 }} />
                    <Box>
                      <Typography variant="h6">
                        Cheques Pendientes de Cobro
                      </Typography>
                      <Typography variant="body2">
                        {resumen.cheques_pendientes} cheque(s) por {formatCurrency(resumen.monto_cheques_pendientes)}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/tesoreria/cheques')}
                  >
                    Ver Cheques
                  </Button>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Accesos Rápidos */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Accesos Rápidos
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/tesoreria/cajas')}
                  >
                    Gestionar Cajas
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/tesoreria/bancos')}
                  >
                    Gestionar Bancos
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/tesoreria/transacciones')}
                  >
                    Ver Transacciones
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/tesoreria/flujo-caja')}
                  >
                    Flujo de Caja
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}

export default Tesoreria;
